/**
 * Registry for managing fields that are dynamically injected into list data
 * This allows components to declare fields they inject so they appear in the field configurator
 */

export interface InjectedFieldMetadata {
	fieldName: string;
	displayName: string;
	description?: string;
	dataType?: 'string' | 'number' | 'boolean' | 'url' | 'array' | 'object';
	category?: string;
	provider?: string; // Component/hook that provides this field
}

class InjectedFieldRegistryClass {
	private readonly fields = new Map<string, InjectedFieldMetadata[]>();
	private readonly listeners: Set<() => void> = new Set();

	/**
	 * Register an injected field for a specific object type
	 */
	registerField(listKey: string, metadata: InjectedFieldMetadata): void {
		if (!metadata.fieldName) {
			throw new Error('Field name is required');
		}

		const fields = this.fields.get(listKey) || [];

		// Check if field already exists and update it
		const existingIndex = fields.findIndex(
			(f) => f.fieldName === metadata.fieldName,
		);
		if (existingIndex >= 0) {
			fields[existingIndex] = metadata;
		} else {
			fields.push(metadata);
		}

		this.fields.set(listKey, fields);
		this.notifyListeners();
	}

	/**
	 * Register multiple injected fields at once
	 */
	registerFields(listKey: string, fields: InjectedFieldMetadata[]): void {
		fields.forEach((field) => { this.registerField(listKey, field); });
	}

	/**
	 * Get all injected fields for an object type
	 */
	getFields(listKey: string): InjectedFieldMetadata[] {
		return this.fields.get(listKey) || [];
	}

	/**
	 * Check if a field is injected (not a database field)
	 */
	isFieldInjected(listKey: string, fieldName: string): boolean {
		const fields = this.getFields(listKey);
		return fields.some((f) => f.fieldName === fieldName);
	}

	/**
	 * Get metadata for a specific injected field
	 */
	getFieldMetadata(
		listKey: string,
		fieldName: string,
	): InjectedFieldMetadata | undefined {
		const fields = this.getFields(listKey);
		return fields.find((f) => f.fieldName === fieldName);
	}

	/**
	 * Clear all registrations for an object type
	 */
	clearObjectType(listKey: string): void {
		this.fields.delete(listKey);
		this.notifyListeners();
	}

	/**
	 * Clear all registrations
	 */
	clear(): void {
		this.fields.clear();
		this.notifyListeners();
	}

	/**
	 * Subscribe to registry changes
	 */
	subscribe(listener: () => void): () => void {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	private notifyListeners(): void {
		this.listeners.forEach((listener) => { listener(); });
	}
}

// Export singleton instance
export const InjectedFieldRegistry = new InjectedFieldRegistryClass();

// React hook for using the registry
import { useEffect, useState } from 'react';

export function useInjectedFields(listKey: string) {
	const [fields, setFields] = useState<InjectedFieldMetadata[]>(() =>
		InjectedFieldRegistry.getFields(listKey),
	);

	useEffect(() => {
		const unsubscribe = InjectedFieldRegistry.subscribe(() => {
			setFields(InjectedFieldRegistry.getFields(listKey));
		});

		return unsubscribe;
	}, [listKey]);

	return fields;
}
